import type { AiToolFieldMaskExpansionConfig } from '@erikdakoda/ai-tool/findManyAiToolInput';
import { namedItemOrderByFieldPaths } from '@erikdakoda/ai-tool/findManyAiToolInput';
import { promptForEditLeafSelect } from './promptForEditSelect';
import {
  BotForAiToolsSearchSelect,
  BotForEditSelect,
  PromptForAiToolsSearchSelect,
  PromptForEditSelect,
} from '../types';

/** Scalar prompt fields agents may request in flat select masks. */
export const PROMPT_AI_TOOL_SCALAR_FIELDS = [
  'id',
  'name',
  'slug',
  'createdAt',
  'updatedAt',
  'publishedAt',
  'private',
  'ownerId',
  'spaceId',
  'description',
  'group',
  'handle',
  'variables',
  'script',
  'text',
] as const;

/** Relation flags for prompt read/edit select masks. */
export const PROMPT_AI_TOOL_RELATION_FIELDS = ['childPrompts', 'versions'] as const;

/** Select expansion for prompt version history on readPrompt. */
export const promptVersionHistorySelect = {
  orderBy: { version: 'desc' as const },
  select: {
    version: true,
    createdAt: true,
    name: true,
    description: true,
    text: true,
  },
} as const;

/** Minimal listing mask fields for prompt search/find tools. */
export const PROMPT_AI_TOOL_SEARCH_SCALAR_FIELDS = [
  'id',
  'name',
  'description',
  'group',
  'handle',
] as const;

/** Prompt fields available in find/filter DSL. */
export const PROMPT_AI_TOOL_FILTER_FIELDS = [
  'id',
  'name',
  'slug',
  'description',
  'group',
  'handle',
  'text',
  'ownerId',
  'spaceId',
  'private',
  'publishedAt',
] as const;

/** Prompt fields agents may sort by in find tools. */
export const PROMPT_AI_TOOL_SORTABLE_FIELDS = [
  'name',
  'createdAt',
  'updatedAt',
  'publishedAt',
  'group',
  'handle',
] as const;

/** Scalar bot fields agents may request in flat select masks. */
export const BOT_AI_TOOL_SCALAR_FIELDS = [
  'id',
  'name',
  'slug',
  'createdAt',
  'updatedAt',
  'publishedAt',
  'private',
  'ownerId',
  'spaceId',
  'description',
  'avatarId',
  'group',
  'handle',
  'integration',
  'modelName',
  'useCase',
  'serviceTier',
  'maxSteps',
  'temperature',
  'reasoningEffort',
  'verbosity',
  'toolAccessMode',
  'systemPromptValues',
  'outputSchema',
  'autoStart',
  'analysisBotGroup',
  'analysisBotHandle',
  'analysisSchema',
  'toolSetId',
] as const;

/** Relation flags for bot read/edit select masks. */
export const BOT_AI_TOOL_RELATION_FIELDS = ['childPrompts'] as const;

/** Minimal listing mask fields for bot search/find tools. */
export const BOT_AI_TOOL_SEARCH_SCALAR_FIELDS = [
  'id',
  'name',
  'description',
  'group',
  'handle',
] as const;

/** Bot fields available in find/filter DSL. */
export const BOT_AI_TOOL_FILTER_FIELDS = [
  'id',
  'name',
  'slug',
  'description',
  'group',
  'handle',
  'ownerId',
  'spaceId',
  'private',
  'publishedAt',
  'integration',
  'modelName',
  'useCase',
] as const;

/** Bot fields agents may sort by in find tools. */
export const BOT_AI_TOOL_SORTABLE_FIELDS = [
  'name',
  'createdAt',
  'updatedAt',
  'publishedAt',
  'group',
  'handle',
  'modelName',
  'useCase',
] as const;

/** Expands prompt flat select masks for read/edit tools. */
export const promptForEditFieldMaskConfig: AiToolFieldMaskExpansionConfig = {
  defaultSelect: PromptForEditSelect,
  scalarFields: PROMPT_AI_TOOL_SCALAR_FIELDS,
  relationFields: PROMPT_AI_TOOL_RELATION_FIELDS,
  relationExpansions: {
    childPrompts: PromptForEditSelect.childPrompts as Readonly<Record<string, unknown>>,
    versions: promptVersionHistorySelect,
  },
};

/** Expands prompt flat select masks for search/find listing tools. */
export const promptForSearchFieldMaskConfig: AiToolFieldMaskExpansionConfig = {
  defaultSelect: PromptForAiToolsSearchSelect,
  scalarFields: PROMPT_AI_TOOL_SEARCH_SCALAR_FIELDS,
};

/** Expands bot flat select masks for read/edit tools. */
export const botForEditFieldMaskConfig: AiToolFieldMaskExpansionConfig = {
  defaultSelect: BotForEditSelect,
  scalarFields: BOT_AI_TOOL_SCALAR_FIELDS,
  relationFields: BOT_AI_TOOL_RELATION_FIELDS,
  relationExpansions: {
    childPrompts: BotForEditSelect.childPrompts as Readonly<Record<string, unknown>>,
  },
};

/** Expands bot flat select masks for search/find listing tools. */
export const botForSearchFieldMaskConfig: AiToolFieldMaskExpansionConfig = {
  defaultSelect: BotForAiToolsSearchSelect,
  scalarFields: BOT_AI_TOOL_SEARCH_SCALAR_FIELDS,
};

/** Leaf scalar keys from prompt edit select (for tests and docs). */
export const promptForEditLeafFieldNames = Object.keys(promptForEditLeafSelect);

export const promptOrderByFieldPaths = {
  ...namedItemOrderByFieldPaths,
  group: ['group'],
  handle: ['handle'],
} as const;

export const botOrderByFieldPaths = {
  ...namedItemOrderByFieldPaths,
  group: ['group'],
  handle: ['handle'],
  modelName: ['modelName'],
} as const;
